/**
 * Where single sign-on lands (2026-09-29). OIDC providers send
 * ?code&state; the SAML endpoint sends a one-time ?ticket. Either way we
 * swap it for a session through the API and go to the app.
 */
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { apiClient } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { apiErrorMessage } from '../utils/apiError'

export default function SsoCallbackPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { signInWithToken } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return // a ticket or code works once; StrictMode runs effects twice
    started.current = true
    const code = params.get('code')
    const state = params.get('state')
    const ticket = params.get('ticket')
    const idpError = params.get('error_description') || params.get('error')

    const finish = async () => {
      if (idpError) throw new Error(`Your identity provider said: ${idpError}`)
      let token: string
      if (ticket) {
        token = (await apiClient.post<{ access_token: string }>('/api/v1/sso/exchange', { ticket })).data.access_token
      } else if (code && state) {
        token = (await apiClient.get<{ access_token: string }>('/api/v1/sso/oidc/callback', { params: { code, state } })).data.access_token
      } else {
        throw new Error('This sign-in link is incomplete.')
      }
      await signInWithToken(token)
      navigate('/', { replace: true })
    }
    finish().catch((err) => setError(err instanceof Error && !('response' in err) ? err.message
      : apiErrorMessage(err, 'Single sign-on didn’t complete.')))
  }, [params, navigate, signInWithToken])

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center">
        {error ? (
          <>
            <h1 className="mb-2 text-lg font-semibold text-foreground">Sign-in didn’t complete</h1>
            <p role="alert" className="mb-4 text-sm text-muted-foreground">{error}</p>
            <Link to="/login" className="text-sm font-medium text-primary underline">Back to sign in</Link>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Signing you in…</p>
        )}
      </div>
    </div>
  )
}
